import React, { useEffect, useState } from "react";
import { ClipboardCopy, DollarSign, Users, ExternalLink } from "lucide-react";
import { formatCurrency } from "../../utils/currency";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Button } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useGlobalState } from "../../GlobalStates";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getReferralAmount } from "../../utils/multi-language";
import { useUserLocale } from "../../customHooks/useUserLocale";

const AffiliateProgram = () => {
  const [user, setUser] = useGlobalState("user");
  const navigate = useNavigate();
  const { language, community, currency, loading, handleChangeLocale } =
    useUserLocale();

  const { t } = useTranslation();

  const [inviteUrl, setInviteUrl] = useState(
    "www.sotravel.com/welcome-discount"
  );
  const [inviteMessage, setInviteMessage] = useState("");
  const [copyIconColor, setCopyIconColor] = useState("black");
  const [_, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");

  const changeCopyColor = () => {
    setOpenAlertBannerMsg(
      t("referral_link_copied", {
        defaultValue: "Referral link copied",
      })
    );
    setCopyIconColor("deepskyblue");
    setTimeout(() => {
      setCopyIconColor("black");
    }, 1200);
  };

  useEffect(() => {
    const getUserInviteLink = async () => {
      if (user.id) {
        const inviteUrl = `www.sotravel.com/welcome-discount?code=${user.id}`;
        const msg = t("invite_message", {
          defaultValue: `Use this link to get {{amount}} off your first Sotravel trip! 👉 ${inviteUrl}`,
          amount: getReferralAmount(currency, "welcome"),
          inviteUrl: inviteUrl,
        });
        setInviteMessage(msg);
        setInviteUrl(inviteUrl);
      }
    };

    getUserInviteLink();
  }, [user.id]);

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <section className="tw-relative tw-h-[70vh] tw-overflow-hidden">
        <div
          className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center tw-transform tw-scale-110"
          style={{
            backgroundImage:
              "url('https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/151028Copy%20of%20IMG_4012.JPG')",
            transform: "translateZ(-1px) scale(1)",
          }}
        />
        <div className="tw-absolute tw-inset-0 tw-bg-black/40" />
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <div className="tw-text-center tw-text-white tw-max-w-4xl tw-px-4">
            <h1 className="tw-text-[34px] md:tw-text-[52px] tw-font-bold tw-mb-3 tw-leading-tight">
              Inspire travelers,
              <br />
              be rewarded
            </h1>
            <p className="tw-text-[18px] md:tw-text-[20px] tw-mb-5 tw-text-gray-200 tw-max-w-2xl tw-mx-auto">
              Turn your influence into unforgettable journeys and meaningful
              rewards
            </p>
            {!user.id ? (
              <button
                onClick={() =>
                  navigate({
                    pathname: "/login",
                    search: `?reroute=/affiliate`,
                  })
                }
                className="tw-bg-[#00BFFF] tw-text-white tw-font-medium tw-py-2.5 tw-px-6 tw-rounded-full tw-text-[16px] hover:tw-bg-[#00A3D9] tw-transition tw-duration-300"
              >
                Be an affiliate
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <div className="tw-max-w-4xl tw-mx-auto tw-px-4 tw-py-8">
        {user.id && (
          <section className="tw-mb-8">
            <h2 className="tw-text-2xl tw-font-semibold tw-mb-4 tw-text-deepskyblue">
              Your affiliate link
            </h2>
            <CopyToClipboard text={inviteMessage} onCopy={changeCopyColor}>
              <button
                className="tw-h-12 tw-py-2 tw-px-4 tw-border tw-border-solid tw-rounded-md tw-mt-4 tw-font-normal tw-text-[15px] tw-font-manrope tw-text-black tw-flex tw-items-center tw-justify-between tw-bg-white tw-w-full md:tw-w-[100%]"
                style={{ borderColor: copyIconColor }}
              >
                <span
                  className="tw-h-full tw-w-[90%] tw-flex tw-items-center tw-justify-start tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-inline-block tw-text-left"
                  style={{ color: copyIconColor }}
                >
                  {inviteUrl}
                </span>
                <ContentCopyOutlinedIcon
                  className="tw-text-[22px]"
                  style={{ color: copyIconColor }}
                />
              </button>
            </CopyToClipboard>
          </section>
        )}

        <section className="tw-mb-8">
          <h2 className="tw-text-2xl tw-font-semibold tw-mb-4 tw-text-deepskyblue">
            How It Works
          </h2>
          <p className="tw-mb-6 tw-text-lg">
            As a travel creator, you can earn rewards by referring customers to
            book their next adventure with us. Below are the details on how to
            participate, track your earnings, and claim your rewards.
          </p>
          <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md">
            <ul className="tw-list-disc tw-list-inside tw-space-y-2">
              <li>Earn 5% of the total revenue from each booking</li>
              <li>
                When your followers use your link, they will get{" "}
                {getReferralAmount(currency, "welcome")} off their first trip
              </li>
            </ul>
          </div>
        </section>

        {/* <section className="tw-mb-8">
          <h2 className="tw-text-2xl tw-font-semibold tw-mb-4 tw-text-deepskyblue">
            Tracking your earnings (In progress)
          </h2>
          <p className="tw-mb-4">
            We are in the process of improving this affiliate portal that will
            allow you to track your earnings in real time. This feature will be
            available by end of April and will include:
          </p>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4">
            <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md tw-flex tw-flex-col tw-items-center">
              <ExternalLink className="tw-mb-2 tw-text-deepskyblue" size={24} />
              <p className="tw-text-center">
                Number of clicks on your referral link
              </p>
            </div>
            <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md tw-flex tw-flex-col tw-items-center">
              <Users className="tw-mb-2 tw-text-deepskyblue" size={24} />
              <p className="tw-text-center">
                Number of bookings attributed to you
              </p>
            </div>
            <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-md tw-flex tw-flex-col tw-items-center">
              <DollarSign className="tw-mb-2 tw-text-deepskyblue" size={24} />
              <p className="tw-text-center">Your total earnings</p>
            </div>
          </div>
        </section>

        <section className="tw-mb-8">
          <h2 className="tw-text-2xl tw-font-semibold tw-mb-4 tw-text-deepskyblue">
            Withdrawal Requests and Payout Status (In progress)
          </h2>
          <p className="tw-mb-4">
            We are in the process of improving this affiliate portal that will
            allow you to create withdrawal requests. This feature will be
            available by end of April and will include:
          </p>
          <ul className="tw-list-disc tw-list-inside tw-space-y-2">
            <li>Submit withdrawal method</li>
            <li>Withdrawing Your Earnings</li>
            <li>See status of withdrawal</li>
          </ul>
        </section> */}

        <footer className="tw-mt-12 tw-text-center">
          <p>
            If you have any questions or need assistance, please reach out to
            our support team!
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AffiliateProgram;
